import React, { useState } from "react";
import { DayPicker } from "react-day-picker";
import { format, parse, isValid } from "date-fns";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { CalendarBlank, X } from "@phosphor-icons/react";
import "react-day-picker/dist/style.css";

// A dark-mode-safe date picker with HARDCODED colours per PM spec.
// Native <input type="date"> renders browser-default text/icon that blends
// into dark backgrounds — this component swaps the invisible native picker
// for a Popover + react-day-picker calendar with fully overridden classes.
// No dynamic-contrast maths anywhere: light-mode and dark-mode variants are
// both hardcoded.
//
// Palette (fixed, do not change):
//   BG:        white light / #1E2130 dark
//   Text:      #111827 light / #F9FAFB dark
//   Border:    #D1D5DB light / #374151 dark
//   Selected:  #3B82F6 bg + white text (both modes)
//   Hover:     #EFF6FF light / #2D3748 dark
export default function DateInput({ value, onChange, placeholder = "Pick a date", "data-testid": testId, className = "" }) {
  const [open, setOpen] = useState(false);

  // value is an ISO date string (YYYY-MM-DD) — same contract as native <input type="date">.
  const parsed = value ? parse(value, "yyyy-MM-dd", new Date()) : undefined;
  const selected = parsed && isValid(parsed) ? parsed : undefined;

  const handleSelect = (d) => {
    if (!d) { onChange?.(""); setOpen(false); return; }
    onChange?.(format(d, "yyyy-MM-dd"));
    setOpen(false);
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange?.("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className={
            // Hardcoded field surface — bg / text / border for both themes.
            "group relative flex h-10 w-full items-center gap-2 rounded-md px-3 text-sm text-left transition-colors " +
            "bg-white text-[#111827] border border-[#D1D5DB] hover:bg-[#EFF6FF] " +
            "dark:bg-[#1E2130] dark:text-[#F9FAFB] dark:border-[#374151] dark:hover:bg-[#2D3748] " +
            "focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:ring-offset-0 " +
            className
          }
        >
          <CalendarBlank size={14} className="shrink-0 text-[#111827] dark:text-[#F9FAFB] opacity-80" />
          <span className={selected ? "" : "opacity-60"}>
            {selected ? format(selected, "dd MMM yyyy") : placeholder}
          </span>
          {selected && (
            <span
              role="button"
              tabIndex={-1}
              aria-label="Clear date"
              onClick={clear}
              className="ml-auto p-1 rounded-sm hover:bg-black/10 dark:hover:bg-white/10"
            >
              <X size={12} />
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className={
          "w-auto p-2 rounded-md shadow-lg " +
          "bg-white text-[#111827] border border-[#D1D5DB] " +
          "dark:bg-[#1E2130] dark:text-[#F9FAFB] dark:border-[#374151]"
        }
      >
        <DayPicker
          mode="single"
          selected={selected}
          onSelect={handleSelect}
          showOutsideDays
          classNames={{
            months: "flex flex-col",
            month: "space-y-2",
            caption: "flex justify-center pt-1 relative items-center text-[#111827] dark:text-[#F9FAFB] font-medium text-sm",
            caption_label: "text-sm font-semibold",
            nav: "space-x-1 flex items-center",
            nav_button:
              "h-7 w-7 rounded-md inline-flex items-center justify-center " +
              "bg-white text-[#111827] border border-[#D1D5DB] hover:bg-[#EFF6FF] " +
              "dark:bg-[#1E2130] dark:text-[#F9FAFB] dark:border-[#374151] dark:hover:bg-[#2D3748] " +
              "transition-colors",
            nav_button_previous: "absolute left-1",
            nav_button_next: "absolute right-1",
            table: "w-full border-collapse",
            head_row: "flex",
            head_cell:
              "w-9 text-[0.72rem] font-normal uppercase tracking-wider " +
              "text-[#111827]/70 dark:text-[#F9FAFB]/70",
            row: "flex w-full mt-1",
            cell: "text-center text-sm p-0",
            day:
              "h-9 w-9 rounded-md inline-flex items-center justify-center text-sm font-normal transition-colors " +
              "text-[#111827] hover:bg-[#EFF6FF] " +
              "dark:text-[#F9FAFB] dark:hover:bg-[#2D3748]",
            day_selected:
              "!bg-[#3B82F6] !text-white hover:!bg-[#3B82F6] hover:!text-white focus:!bg-[#3B82F6] focus:!text-white",
            day_today: "ring-1 ring-inset ring-[#3B82F6]/60",
            day_outside: "opacity-40",
            day_disabled: "opacity-30 cursor-not-allowed",
            day_hidden: "invisible",
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
